import React from 'react';
import { Text, Stack, Container, Grid, Link } from 'basis';
import { LayoutContent } from 'components';
import { useTheme } from '@emotion/react';
import { byCard, getProductName, byCountry } from 'utils/byConfig';
import { canGoBackToMerchant } from 'utils';
import { useDataLayerAndAnalyticsLinks } from 'hooks';
import { PRODUCT_CODE_LOW_RATE } from '_config';
import { ExternalLinkIcon } from '../../../components/icons';

export const OutcomeDeclined = ({ storeState }) => {
  const theme = useTheme();

  const OutcomeDeclinedAU = () => {
    const { applicationRef, applyAndBuy } = storeState;
    const productName = getProductName();
    const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);

    return (
      <Container margin="0 0 0 0" hasBreakpointWidth>
        <LayoutContent rowsGap="0">
          <Container padding="0" margin="0">
            <Grid preset="page" colsGap={11}>
              <Grid.Item colSpan="all">
                <Grid rowsGap={6}>
                  <Container padding="8 0 0 0">
                    <Stack gap="8">
                      <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
                        Thank you for your recent application.
                      </Text>
                      <Text>
                        Application reference number: <strong data-hj-suppress>{`${applicationRef}`}</strong>
                      </Text>
                      <Text>
                        Thank you for applying for a {productName}{' '}
                        {byCard({
                          [PRODUCT_CODE_LOW_RATE]: null,
                          default: 'credit card',
                        })}
                        . We appreciate your interest in applying with us. However, on this occasion, we cannot approve
                        your application.
                      </Text>
                      <Text>
                        More details will be sent to your email address and if your circumstances change, you are
                        welcome to reapply in the future.
                      </Text>
                      {canGoBackToMerchant(storeState) && (
                        <>
                          <Text>
                            To continue shopping online at {applyAndBuy?.merchantGroupName} using another form of
                            payment, click the button below.
                          </Text>
                          <Container>
                            <Link
                              href={applyAndBuy.upstreamUrl}
                              appearance="primary-button"
                              testId="return-to-merchant-btn"
                              margin="6 0"
                              onClick={() => {
                                trackEventSender({
                                  location: 'outcome',
                                  label: `Return to ${applyAndBuy?.merchantGroupName}`,
                                  action: 'external-link',
                                });
                              }}
                              newTab
                            >
                              Return to {applyAndBuy?.merchantGroupName}{' '}
                              <ExternalLinkIcon style={{ marginLeft: '8px' }} />
                            </Link>
                          </Container>
                        </>
                      )}
                    </Stack>
                  </Container>
                </Grid>
              </Grid.Item>
            </Grid>
          </Container>
        </LayoutContent>
      </Container>
    );
  };

  const OutcomeDeclinedNZV2 = () => {
    const { applicationRef, applyAndBuy } = storeState;
    const productName = getProductName();
    const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);

    return (
      <Container margin="0 0 0 0" hasBreakpointWidth>
        <LayoutContent rowsGap="0">
          <Container padding="0" margin="0">
            <Grid preset="page" colsGap={11}>
              <Grid.Item colSpan="all">
                <Grid rowsGap={6}>
                  <Container padding="8 0 0 0">
                    <Stack gap="8">
                      <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
                        Thank you for your recent application.
                      </Text>
                      <Text>
                        Application reference number: <strong data-hj-suppress>{`${applicationRef}`}</strong>
                      </Text>
                      <Text>
                        Thank you for applying for a {productName} credit card. We appreciate your interest in applying
                        with us. However, on this occasion, we cannot approve your application.
                      </Text>
                      <Text>
                        Further confirmation will be sent to your email address and if your circumstances change, you
                        are welcome to reapply in the future.
                      </Text>
                      {canGoBackToMerchant(storeState) && (
                        <>
                          <Text>
                            To continue shopping online at {applyAndBuy?.merchantGroupName} using another form of
                            payment, click the button below.
                          </Text>
                          <Container>
                            <Link
                              href={applyAndBuy.upstreamUrl}
                              appearance="primary-button"
                              testId="return-to-merchant-btn"
                              margin="6 0"
                              onClick={() => {
                                trackEventSender({
                                  location: 'outcome',
                                  label: `Return to ${applyAndBuy?.merchantGroupName}`,
                                  action: 'external-link',
                                });
                              }}
                              newTab
                            >
                              Return to {applyAndBuy?.merchantGroupName}{' '}
                              <ExternalLinkIcon style={{ marginLeft: '8px' }} />
                            </Link>
                          </Container>
                        </>
                      )}
                    </Stack>
                  </Container>
                </Grid>
              </Grid.Item>
            </Grid>
          </Container>
        </LayoutContent>
      </Container>
    );
  };

  return byCountry({
    AU: <OutcomeDeclinedAU storeState={storeState} />,
    NZ: <OutcomeDeclinedNZV2 storeState={storeState} />,
  });
};
